import { useState } from "react";
import { cleanCreatorName, deriveTopic } from "./CoachingPanel";
import "./MomentCard.css";

// Coach Mode card — image-forward "Trello-style" tile. Big cover image
// (the video thumbnail from the source) with a clean title strip below
// matching the editor brief header. Tap anywhere on the card to open
// the full ClipCoach session.
//
// The panel itself is rendered at the ForYouPage level so cross-mode
// keyboard nav (← / → across Coach + Clipper cards in one go) lives
// in a single source-of-truth and doesn't fight per-card local state.

export default function MomentCard({
  clip,
  index,
  job,
  isSelected = false,
  isLiked = false,
  onOpen,
  onToggleSelect,
  onToggleLike,
  onDelete,
}) {
  const [imgErrored, setImgErrored] = useState(false);

  // Cover image priority: brand-pulled thumbnail from the job. If that
  // failed to load (CORS / removed video / etc.), fall back to a tinted
  // gradient with the first letter of the brand/streamer name.
  const coverSrc = job?.videoThumbnail && !imgErrored ? job.videoThumbnail : null;
  const brandName = cleanCreatorName(job?.streamerName || job?.videoAuthor || "");
  const initial   = (brandName || "C").trim().charAt(0).toUpperCase();
  const topic     = deriveTopic(clip);
  const titleLine = `${(brandName || "CLIP").toUpperCase()} CLIP ${(index ?? 0) + 1}${topic ? ` ${topic}` : ""}`;

  const stop = (e) => e.stopPropagation();
  const open = () => onOpen?.(clip, job, index);

  return (
    <div
      className={`moment-card moment-card--clickable${isSelected ? " moment-card--selected" : ""}`}
      onClick={open}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") open(); }}
    >
      {/* Cover image area (image OR gradient initial) */}
      <div className="moment-card-cover">
        {coverSrc ? (
          <img
            src={coverSrc}
            alt={brandName}
            className="moment-card-cover-img"
            draggable={false}
            onError={() => setImgErrored(true)}
          />
        ) : (
          <div className="moment-card-cover-fallback" aria-hidden="true">
            <span className="moment-card-cover-initial">{initial}</span>
          </div>
        )}

        {/* Top-right action stack — select / heart / delete.
            Each button stops propagation so the card click doesn't fire. */}
        <div className="moment-card-actions" onClick={stop}>
          {onToggleSelect && (
            <button
              type="button"
              className={`moment-card-action moment-card-action--check${isSelected ? " on" : ""}`}
              onClick={(e) => { e.stopPropagation(); onToggleSelect(e); }}
              title={isSelected ? "Deselect" : "Select"}
              aria-label="Select moment"
            >
              {isSelected && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          )}
          {onToggleLike && (
            <button
              type="button"
              className={`moment-card-action${isLiked ? " moment-card-action--liked" : ""}`}
              onClick={(e) => { e.stopPropagation(); onToggleLike(e); }}
              title={isLiked ? "Unlike" : "Like"}
              aria-label="Like moment"
            >
              <svg width="13" height="13" viewBox="0 0 24 24"
                fill={isLiked ? "currentColor" : "none"}
                stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="moment-card-action moment-card-action--delete"
              onClick={(e) => { e.stopPropagation(); onDelete(e); }}
              title="Delete moment"
              aria-label="Delete moment"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Bottom info bar — Trello-style title strip mirrors the
          CoachingPanel header so the editor sees a consistent
          "AMIR CLIP 2 LONG AGO 90S" label from list to detail.
          Timecode lives inside the panel itself (hook/retention/
          payoff each carry their own [m:ss]) — no need on the tile. */}
      <div className="moment-card-meta">
        <p className="moment-card-title">{titleLine}</p>
      </div>
    </div>
  );
}
